import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAttributes, type AttributeGroup, type BulkResult } from '../../api/catalog';
import { api, ApiError } from '../../api/client';
import { useSession } from '../../auth/session';
import { Alert, Button, Chip, Empty, Note, PageHeader, Panel, Spinner, Table } from '../../ui';
import { capped } from '../../api/write-keys';
import {
  ABOUT_SHEET, NOT_IMPORTED, PRODUCT_COLUMNS, TEMPLATE_VERSION, VARIANT_COLUMNS,
  buildSubmission, errorFileRows, parseCsv, placeRefusals, sheetKind, toCsv,
  type RawSheet, type RowError, type SheetKind, type Submission,
} from './bulk-upload';

/**
 * A-11 — Bulk upload (BU-01…BU-08). Load or extend the catalog from the workbook,
 * see every row that would fail and why, and only then write.
 *
 * Built on the route that already serves A-09's variant grid (`POST /catalog/import`,
 * floored on `product.bulk`), not a second path beside it. What this screen adds is
 * the file: reading it in the browser, naming the row and column of every failure
 * the browser can see, and handing the server one submission.
 *
 * Two steps on purpose (BU-04). **Check** is the server's dry run — every rule, no
 * write. **Import** sends the same rows again for real, and valid rows land while
 * refused ones come back (BU-05), because on a catalog of hundreds of variants one
 * bad row rejecting the whole file makes the feature unusable.
 *
 * What it does NOT do yet, said on screen rather than discovered: update rows that
 * already exist (BU-03 — they are reported "already in the catalog" and left alone),
 * price-only updates (BU-06), and images by ZIP (BU-08). Stones, certificates, images
 * and sizes are entered on A-09.
 */

type Placed = ReturnType<typeof placeRefusals>;
type Loaded = { files: string[]; sheets: RawSheet[]; version: string | null; submission: Submission };
type Checked = { result: BulkResult; refusals: Placed };

/** A workbook cell as the text a person typed. A number is read back without formatting. */
function cellText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'object') {
    const o = v as { result?: unknown; richText?: Array<{ text: string }>; text?: string };
    if (o.richText) return o.richText.map((t) => t.text).join('');
    if (o.result !== undefined) return cellText(o.result);
    if (typeof o.text === 'string') return o.text;
  }
  return '';
}

/** Reads every sheet of a workbook, or the one table of a CSV, into plain rows. */
async function readFile(file: File): Promise<RawSheet[]> {
  if (file.name.toLowerCase().endsWith('.csv')) {
    return [{ name: file.name, rows: parseCsv(await file.text()) }];
  }
  // Loaded on first use, like the order export: it stays out of every other screen's bundle.
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const sheets: RawSheet[] = [];
  wb.eachSheet((ws) => {
    const rows: string[][] = [];
    ws.eachRow({ includeEmpty: false }, (row) => {
      const values = row.values as unknown[];
      // exceljs rows are 1-based: index 0 is always empty.
      rows.push(values.slice(1).map(cellText));
    });
    sheets.push({ name: ws.name, rows });
  });
  return sheets;
}

/** BU-02 — the template's version marker, read from its About sheet. */
function versionOf(sheets: readonly RawSheet[]): string | null {
  const about = sheets.find((s) => s.name === ABOUT_SHEET);
  const row = about?.rows.find((r) => r[0]?.trim() === 'template_version');
  return row?.[1]?.trim() || null;
}

function download(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * BU-02 — the template, built from THIS portal's live option lists, so a value the
 * portal has switched off is never offered as a choice. Every column is text so a
 * carat of `010` stays `010` and is not read back as the number ten.
 */
async function downloadTemplate(groups: readonly AttributeGroup[], portalName: string) {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Miraki';
  wb.created = new Date();
  const sheet = (name: string, columns: readonly string[]) => {
    const ws = wb.addWorksheet(name, { views: [{ state: 'frozen', ySplit: 1 }] });
    ws.columns = columns.map((c) => ({ header: c, key: c, width: Math.max(14, c.length + 4), style: { numFmt: '@' } }));
    ws.getRow(1).font = { bold: true };
    return ws;
  };
  sheet('1 Products', PRODUCT_COLUMNS);
  sheet('2 Variants', VARIANT_COLUMNS);
  const ref = sheet('Reference', ['column', 'accepted value', 'shown as']);
  const columnFor: Record<string, string> = {
    purity: 'purities / purity', mcol: 'colours / metal_colour', carat: 'carats / centre_stone_carat',
    setting: 'setting', finish: 'finish', clasp: 'clasp', backing: 'backing', hallmark: 'hallmark',
  };
  for (const g of groups) {
    const column = columnFor[g.key];
    if (!column) continue;
    for (const v of g.values) ref.addRow([column, v.code, v.label]);
  }
  const about = wb.addWorksheet(ABOUT_SHEET);
  about.addRows([
    ['template_version', TEMPLATE_VERSION],
    ['portal', portalName],
    ['generated', new Date().toISOString().slice(0, 10)],
    ['lists', 'purities, colours and carats take several codes separated by |'],
  ]);
  const buffer = await wb.xlsx.writeBuffer();
  download(`miraki-catalog-template-${TEMPLATE_VERSION}.xlsx`, new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
}

const SHEET_LABEL: Record<SheetKind, string> = { products: 'Products', variants: 'Variants' };

export function BulkUpload() {
  const { can, portal } = useSession();
  const { data: groups, isLoading } = useAttributes();
  const qc = useQueryClient();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [checked, setChecked] = useState<Checked | null>(null);
  const [imported, setImported] = useState<Checked | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (isLoading || !groups) return <Spinner />;
  const mayLoad = can('product.bulk');

  const run = (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    void (async () => {
      try { await fn(); } catch (e) {
        setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'That did not work');
      } finally { setBusy(false); }
    })();
  };

  const onFiles = (list: FileList | null) => {
    // Copied now: a FileList is live, and the input is cleared (so the same file can be chosen
    // again) before the parse below has finished with it.
    const files = list ? [...list] : [];
    if (!files.length) return;
    setLoaded(null); setChecked(null); setImported(null);
    run(async () => {
      const sheets = (await Promise.all(files.map(readFile))).flat();
      if (!sheets.some((s) => sheetKind(s.rows[0] ?? []) !== null)) {
        throw new Error('No Products or Variants sheet found. Start from the template below.');
      }
      setLoaded({
        files: files.map((f) => f.name), sheets,
        version: versionOf(sheets), submission: buildSubmission(sheets, groups),
      });
    });
  };

  const send = (dryRun: boolean) => run(async () => {
    if (!loaded) return;
    const { products, variants, rowOf } = loaded.submission;
    const result = await api.post<BulkResult>('/catalog/import', { products, variants, dryRun });
    const out = { result, refusals: placeRefusals(result.refused, rowOf) };
    if (dryRun) { setChecked(out); return; }
    setImported(out);
    // The products list, the editor and its variants all read what this wrote.
    await capped(qc.invalidateQueries({
      predicate: (q) => q.queryKey[2] === 'catalog'
        && ['products', 'product', 'variants'].includes(String(q.queryKey[3])),
    }));
  });

  const staleVersion = loaded?.version !== null && loaded?.version !== undefined && loaded.version !== TEMPLATE_VERSION;
  const parseErrors: RowError[] = loaded?.submission.errors ?? [];
  const latest = imported ?? checked;
  const toSend = loaded ? loaded.submission.products.length + loaded.submission.variants.length : 0;

  return (
    <>
      <PageHeader
        title="Bulk upload"
        // The portal, which is the one thing here a person cannot see elsewhere on the screen
        // and must be sure of before loading a file into it.
        sub={portal?.name}
      />
      {!mayLoad ? (
        <Alert tone="stop">Loading the catalog from a file needs <code>product.bulk</code>, which your roles do not grant.</Alert>
      ) : null}
      {error ? <Alert tone="stop">{error}</Alert> : null}

      <Panel title="1. Template" pad>
        <Note>
          Fill <b>1 Products</b> (one row per design) and <b>2 Variants</b> (one row per priced piece). The
          purities, colours and carats on a product are the values it is <b>offered in</b>; several are separated
          with <code>|</code>. Leave <code>variant_code</code> blank to have it derived. Stones, certificates,
          images and {NOT_IMPORTED.join(', ')} are entered on the product screen, not here.
        </Note>
        <Button tone="ghost" disabled={busy} onClick={() => run(() => downloadTemplate(groups, portal?.name ?? ''))}>
          Download template
        </Button>{' '}
        <span className="mk-hint">Version {TEMPLATE_VERSION}, with this portal&rsquo;s accepted values.</span>
      </Panel>

      <Panel title="2. File" pad>
        <input
          type="file" accept=".xlsx,.csv" multiple disabled={!mayLoad || busy}
          onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }}
        />
        {loaded ? (
          <div style={{ marginTop: 'var(--mk-space-3)' }}>
            <Note>
              {loaded.files.join(', ')} — {loaded.submission.products.length} products and{' '}
              {loaded.submission.variants.length} variants ready to check
              {parseErrors.length ? `, ${parseErrors.length} rows set aside below` : ''}.
              {loaded.version === null ? ' This file carries no template version, so it is read against the current columns.' : ''}
            </Note>
            {staleVersion ? (
              <Alert tone="stop">
                This file is template version <b>{loaded.version}</b>; the current one is <b>{TEMPLATE_VERSION}</b>.
                Download the template again and move the rows across — the columns may have changed.
              </Alert>
            ) : null}
          </div>
        ) : null}
      </Panel>

      {loaded && !staleVersion ? (
        <Panel title="3. Check, then import" pad>
          <Button disabled={busy || !mayLoad || toSend === 0} onClick={() => send(true)}>Check file</Button>{' '}
          <Button
            disabled={busy || !mayLoad || !checked || imported !== null
              || checked.result.productsCreated + checked.result.variantsCreated === 0}
            onClick={() => send(false)}
          >
            Import valid rows
          </Button>{' '}
          {busy ? <span className="mk-hint">Working…</span> : null}

          {latest ? (
            <div style={{ marginTop: 'var(--mk-space-4)' }}>
              <Alert tone={imported ? 'done' : 'us'}>
                <b>{imported ? 'Imported.' : 'Checked — nothing written yet.'}</b>{' '}
                {latest.result.productsCreated} products and {latest.result.variantsCreated} variants
                {imported ? ' created' : ' would be created'}
                {latest.result.productsExisting + latest.result.variantsExisting > 0
                  ? `; ${latest.result.productsExisting} products and ${latest.result.variantsExisting} variants are already in the catalog and are left as they are`
                  : ''}
                {latest.refusals.length ? `; ${latest.refusals.length} refused` : ''}.
                {imported && latest.result.productsCreated > 0
                  ? ' New products start unpublished — publish them from the product list when they are ready.' : ''}
              </Alert>
            </div>
          ) : null}
        </Panel>
      ) : null}

      {loaded && (parseErrors.length > 0 || (latest?.refusals.length ?? 0) > 0) ? (
        <Panel title="Rows that will not go in" pad>
          <Button
            tone="ghost"
            onClick={() => download('miraki-catalog-errors.csv', new Blob(
              [toCsv(errorFileRows(parseErrors, latest?.refusals ?? []))], { type: 'text/csv' },
            ))}
          >
            Download error file
          </Button>
          <Table<{ key: string; sheet: string; row: string; column: string; ref: string; reason: string; by: 'file' | 'server' }>
            rows={[
              ...parseErrors.map((e, i) => ({
                key: `p${i}`, sheet: SHEET_LABEL[e.sheet], row: String(e.row), column: e.column, ref: '', reason: e.reason, by: 'file' as const,
              })),
              ...(latest?.refusals ?? []).map((r, i) => ({
                key: `s${i}`, sheet: r.sheet ? SHEET_LABEL[r.sheet] : '', row: r.row === null ? '—' : String(r.row),
                column: '', ref: r.ref, reason: r.reason, by: 'server' as const,
              })),
            ]}
            rowKey={(r) => r.key}
            columns={[
              { header: 'Sheet', render: (r) => r.sheet },
              { header: 'Row', numeric: true, render: (r) => r.row },
              { header: 'Column', render: (r) => (r.column ? <code>{r.column}</code> : <span className="mk-dim">—</span>) },
              { header: 'Reference', render: (r) => (r.ref ? <code>{r.ref}</code> : <span className="mk-dim">—</span>) },
              { header: 'Reason', render: (r) => r.reason },
              { header: '', render: (r) => <Chip tone={r.by === 'file' ? 'lapse' : 'stop'}>{r.by === 'file' ? 'not sent' : 'refused'}</Chip> },
            ]}
          />
        </Panel>
      ) : null}

      {!loaded ? <Empty title="No file yet" body="Choose the filled-in template, or its two sheets as CSV files." /> : null}
    </>
  );
}
